import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AppShell } from '../layouts/AppShell';
import { PublicShell } from '../layouts/PublicShell';
import { ProtectedRoute } from './ProtectedRoute';
import { PageLoader } from './PageLoader';

/**
 * Every page below is code-split so the initial JS payload only carries the
 * login page + AppShell chrome. Interior modules stream in when the user
 * actually navigates to them — an employee never downloads the org settings
 * page, HR admins never download the manager team grid, etc.
 *
 * The named-export → `default` shim on each lazy import is because React.lazy
 * only understands default exports; keeping named exports in the page files
 * lets test imports and direct references stay ergonomic.
 */

const LoginPage = lazy(() =>
  import('../pages/login/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const DashboardPage = lazy(() =>
  import('../pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const InboxPage = lazy(() =>
  import('../pages/inbox/InboxPage').then((m) => ({ default: m.InboxPage })),
);
const CelebrationsPage = lazy(() =>
  import('../pages/celebrations/CelebrationsPage').then((m) => ({
    default: m.CelebrationsPage,
  })),
);
const MyProfilePage = lazy(() =>
  import('../pages/me/MyProfilePage').then((m) => ({ default: m.MyProfilePage })),
);
const EmployeesPage = lazy(() =>
  import('../pages/employees/EmployeesPage').then((m) => ({ default: m.EmployeesPage })),
);
const EmployeeProfilePage = lazy(() =>
  import('../pages/employees/EmployeeProfilePage').then((m) => ({
    default: m.EmployeeProfilePage,
  })),
);
const EmployeeEditPage = lazy(() =>
  import('../pages/employees/EmployeeEditPage').then((m) => ({
    default: m.EmployeeEditPage,
  })),
);
const AttendancePage = lazy(() =>
  import('../pages/attendance/AttendancePage').then((m) => ({ default: m.AttendancePage })),
);
const LeavePage = lazy(() =>
  import('../pages/leave/LeavePage').then((m) => ({ default: m.LeavePage })),
);
const OrgSettingsPage = lazy(() =>
  import('../pages/settings/OrgSettingsPage').then((m) => ({ default: m.OrgSettingsPage })),
);
const PlaceholderPage = lazy(() =>
  import('../pages/PlaceholderPage').then((m) => ({ default: m.PlaceholderPage })),
);
const PayrollPage = lazy(() =>
  import('../pages/payroll/PayrollPage').then((m) => ({ default: m.PayrollPage })),
);
const ExpensesPage = lazy(() =>
  import('../pages/expenses/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
);
const OnboardingPage = lazy(() =>
  import('../pages/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const ExitsPage = lazy(() =>
  import('../pages/exits/ExitsPage').then((m) => ({ default: m.ExitsPage })),
);
const DocumentsPage = lazy(() =>
  import('../pages/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
);
const AssetsPage = lazy(() =>
  import('../pages/assets/AssetsPage').then((m) => ({ default: m.AssetsPage })),
);
const HiringPage = lazy(() =>
  import('../pages/hiring/HiringPage').then((m) => ({ default: m.HiringPage })),
);
const PerformancePage = lazy(() =>
  import('../pages/performance/PerformancePage').then((m) => ({ default: m.PerformancePage })),
);
const EngagePage = lazy(() =>
  import('../pages/engage/EngagePage').then((m) => ({ default: m.EngagePage })),
);
const HelpdeskPage = lazy(() =>
  import('../pages/helpdesk/HelpdeskPage').then((m) => ({ default: m.HelpdeskPage })),
);
const ProjectsPage = lazy(() =>
  import('../pages/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const OperationsPage = lazy(() =>
  import('../pages/operations/OperationsPage').then((m) => ({ default: m.OperationsPage })),
);
const OperationsProjectPage = lazy(() =>
  import('../pages/operations/OperationsProjectPage').then((m) => ({
    default: m.OperationsProjectPage,
  })),
);
const PlanningPage = lazy(() =>
  import('../pages/planning/PlanningPage').then((m) => ({ default: m.PlanningPage })),
);
const AuditLogsPage = lazy(() =>
  import('../pages/audit/AuditLogsPage').then((m) => ({ default: m.AuditLogsPage })),
);
const SettingsPage = lazy(() =>
  import('../pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const EmployeeAnalyticsPage = lazy(() =>
  import('../pages/employees/EmployeeAnalyticsPage').then((m) => ({
    default: m.EmployeeAnalyticsPage,
  })),
);
const ReportsPage = lazy(() =>
  import('../pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const CareersHome = lazy(() =>
  import('../pages/careers/CareersHome').then((m) => ({ default: m.CareersHome })),
);
const CareersPostingPage = lazy(() =>
  import('../pages/careers/CareersPostingPage').then((m) => ({ default: m.CareersPostingPage })),
);
const OfferAcceptPage = lazy(() =>
  import('../pages/careers/OfferAcceptPage').then((m) => ({ default: m.OfferAcceptPage })),
);

const suspended = (node: React.ReactNode): React.ReactNode => (
  <Suspense fallback={<PageLoader />}>{node}</Suspense>
);

const router = createBrowserRouter([
  {
    element: <PublicShell />,
    children: [
      { path: '/login', element: suspended(<LoginPage />) },
      { path: '/careers', element: suspended(<CareersHome />) },
      { path: '/careers/:slug', element: suspended(<CareersPostingPage />) },
      { path: '/careers/offer/:id', element: suspended(<OfferAcceptPage />) },
    ],
  },
  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { path: '/', element: suspended(<DashboardPage />) },
      { path: '/inbox', element: suspended(<InboxPage />) },
      { path: '/celebrations', element: suspended(<CelebrationsPage />) },
      { path: '/me', element: suspended(<MyProfilePage />) },
      {
        path: '/employees',
        element: (
          <ProtectedRoute requiresModules={['employees']}>
            {suspended(<EmployeesPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/employees/analytics',
        element: (
          <ProtectedRoute requiresModules={['employees']}>
            {suspended(<EmployeeAnalyticsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/employees/:id',
        element: (
          <ProtectedRoute requiresModules={['employees']}>
            {suspended(<EmployeeProfilePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/employees/:id/edit',
        element: (
          <ProtectedRoute requiresModules={['employees']}>
            {suspended(<EmployeeEditPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/attendance',
        element: (
          <ProtectedRoute requiresModules={['attendance']}>
            {suspended(<AttendancePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/leave',
        element: (
          <ProtectedRoute requiresModules={['leave']}>
            {suspended(<LeavePage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/payroll', element: <Navigate to="/payroll/overview" replace /> },
      {
        path: '/payroll/:tab',
        element: (
          <ProtectedRoute requiresModules={['payroll']}>
            {suspended(<PayrollPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/expenses', element: <Navigate to="/expenses/claims" replace /> },
      {
        path: '/expenses/:tab',
        element: (
          <ProtectedRoute requiresModules={['expenses']}>
            {suspended(<ExpensesPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/onboarding', element: <Navigate to="/onboarding/portal" replace /> },
      {
        path: '/onboarding/:tab',
        element: (
          <ProtectedRoute requiresModules={['onboarding']}>
            {suspended(<OnboardingPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/exits', element: <Navigate to="/exits/mine" replace /> },
      {
        path: '/exits/:tab',
        element: (
          <ProtectedRoute requiresModules={['exits']}>
            {suspended(<ExitsPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/documents', element: <Navigate to="/documents/library" replace /> },
      {
        path: '/documents/:tab',
        element: (
          <ProtectedRoute requiresModules={['documents']}>
            {suspended(<DocumentsPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/assets', element: <Navigate to="/assets/mine" replace /> },
      {
        path: '/assets/:tab',
        element: (
          <ProtectedRoute requiresModules={['assets']}>
            {suspended(<AssetsPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/hiring', element: <Navigate to="/hiring/pipeline" replace /> },
      {
        path: '/hiring/:tab',
        element: (
          <ProtectedRoute requiresModules={['hiring']}>
            {suspended(<HiringPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/performance', element: <Navigate to="/performance/goals" replace /> },
      {
        path: '/performance/:tab',
        element: (
          <ProtectedRoute requiresModules={['performance']}>
            {suspended(<PerformancePage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/engage', element: <Navigate to="/engage/announcements" replace /> },
      {
        path: '/engage/:tab',
        element: (
          <ProtectedRoute requiresModules={['engage']}>
            {suspended(<EngagePage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/helpdesk', element: <Navigate to="/helpdesk/my-tickets" replace /> },
      {
        path: '/helpdesk/:tab',
        element: (
          <ProtectedRoute requiresModules={['helpdesk']}>
            {suspended(<HelpdeskPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/projects', element: <Navigate to="/projects/projects" replace /> },
      {
        path: '/projects/:tab',
        element: (
          <ProtectedRoute requiresModules={['projects']}>
            {suspended(<ProjectsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/operations',
        element: (
          <ProtectedRoute requiresModules={['operations']}>
            {suspended(<OperationsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/operations/projects/:id',
        element: (
          <ProtectedRoute requiresModules={['operations']}>
            {suspended(<OperationsProjectPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/planning',
        element: (
          <ProtectedRoute requiresModules={['planning']}>
            {suspended(<PlanningPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/org', element: <Navigate to="/org/departments" replace /> },
      {
        path: '/org/:tab',
        element: (
          <ProtectedRoute requiresModules={['org-structure']}>
            {suspended(<OrgSettingsPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '/settings', element: <Navigate to="/settings/company" replace /> },
      {
        path: '/settings/:tab',
        element: (
          <ProtectedRoute requiresModules={['settings']}>
            {suspended(<SettingsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/audit-logs',
        element: (
          <ProtectedRoute requiresModules={['audit-logs']}>
            {suspended(<AuditLogsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: '/reports',
        element: (
          // Must match the sidebar nav gate in layouts/nav-items.ts — role-specific
          // report submodules (reports:hiring for recruiter, reports:payroll for
          // payroll admin, etc.) also grant access. Otherwise a recruiter who
          // sees the nav item gets bounced back to home when they click it.
          <ProtectedRoute
            requiresModules={[
              'reports',
              'reports:payroll',
              'reports:hiring',
              'reports:assets',
              'reports:team',
            ]}
          >
            {suspended(<ReportsPage />)}
          </ProtectedRoute>
        ),
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
